import React from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { PriorityIconContainer } from '../../../../common/ui/styled';
import type { Priority, PriorityMappings } from '../../../../common/ui/types';
import { MappingPicker } from './mapping-picker';
import messages from './messages';

type MappingRowProps = {
	/**
	 * Represent the current priority the needs to be mapped
	 */
	priority: Priority;
	/**
	 * Represents the priorities that can be selected or mapped to. This is used to
	 * render the dropdown menu options.
	 */
	mappablePriorities: Priority[];
	/**
     A record of priority mappings for the current scheme.
     */
	mappings: PriorityMappings;
	/**
	 * Updates the mappings record
	 * @param currentPriorityId
	 * @param newPriority
	 */
	onUpdateMapping: (currentPriorityId: string, newPriority: Priority) => void;
};

const MappingRow = ({
	priority,
	mappablePriorities,
	mappings,
	onUpdateMapping,
}: MappingRowProps) => {
	const { formatMessage } = useIntl();

	return (
		<MappingRowContainer>
			<MappingCurrentPriorityRowCellContainer>
				<PriorityIconContainer src={priority.iconUrl} alt={priority.name} />
				{priority.name}
			</MappingCurrentPriorityRowCellContainer>
			<MappingTableArrowContainer>
				<ArrowRightIcon size="small" label={formatMessage(messages.mappingTableArrowIcon)} />
			</MappingTableArrowContainer>
			<MappingPicker
				selectedPriority={mappings[priority.id] ? mappings[priority.id] : null}
				mappablePriorities={mappablePriorities}
				setSelectedPriority={(newPriority: Priority) => onUpdateMapping(priority.id, newPriority)}
			/>
		</MappingRowContainer>
	);
};

export type MappingListProps = {
	/**
	 * A list of priorities that need to be mapped.
	 */
	priorities: Priority[];
	/**
	 * Represents the priorities that can be selected or mapped to. This is used to
	 * render the dropdown menu options.
	 */
	mappablePriorities: Priority[];
	/**
	 * A record of priority mappings for the current scheme.
	 */
	mappings: PriorityMappings;
	/**
	 * Updates the mappings record
	 * @param currentPriorityId
	 * @param newPriority
	 */
	onUpdateMapping: (currentPriorityId: string, newPriority: Priority) => void;
};

export const MappingList = ({
	priorities,
	mappablePriorities,
	mappings,
	onUpdateMapping,
}: MappingListProps) => {
	const { formatMessage } = useIntl();

	return (
		<MappingListContainer>
			<MappingListHeaders>
				<MappingCurrentPriorityRowHeaderCellContainer>
					<Heading level="h200">
						{formatMessage(messages.mappingModalCurrentPriorityHeading)}
					</Heading>
				</MappingCurrentPriorityRowHeaderCellContainer>
				<MappingNewPriorityRowHeaderCellContainer>
					<Heading level="h200">{formatMessage(messages.mappingModalNewPriorityHeading)}</Heading>
				</MappingNewPriorityRowHeaderCellContainer>
			</MappingListHeaders>
			{priorities.map((priority: Priority, index: number) => (
				<MappingRow
					key={index}
					priority={priority}
					mappablePriorities={mappablePriorities}
					mappings={mappings}
					onUpdateMapping={onUpdateMapping}
				/>
			))}
		</MappingListContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingListContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingListHeaders = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexDirection: 'row',
	height: token('space.500', '40px'),
	borderBottom: `2px solid ${token('color.border', '#dfe1e6')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingRowContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexDirection: 'row',
	height: token('space.800', '64px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingCurrentPriorityRowCellContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '150px',
	paddingLeft: token('space.300', '24px'),
	gap: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingCurrentPriorityRowHeaderCellContainer = styled.div({
	width: '150px',
	paddingLeft: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingNewPriorityRowHeaderCellContainer = styled.div({
	width: '198px',
	paddingLeft: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingTableArrowContainer = styled.div({
	flexGrow: '1',
	textAlign: 'center',
});
